import { ScrollView, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { ModalBottom } from '@ui/components/Modal'
import Button from '@ui/buttons/Button'
import Typography from '@ui/typography'
import FormattedText from '@ui/typography/FormattedText'
import Gradient from '@ui/effects/Gradient'
import { colors } from '@ui/colors'

export interface PolicyReadModalPolicy {
  key: string
  required: boolean
  revision: string
  acceptedAt: string | null
}

interface Props {
  open: boolean
  close: () => void
  policy: PolicyReadModalPolicy | null
  isDraftAccepted: boolean
  onAccept: () => void
  formatDate: (date: string) => string
}

export default function PolicyReadModal({
  open,
  close,
  policy,
  isDraftAccepted,
  onAccept,
  formatDate,
}: Props) {
  const { t } = useTranslation(['ui', 'modals', 'deals-modals', 'auth'])

  if (!policy) {
    return null
  }

  const acceptedAtDate = policy.acceptedAt ? formatDate(policy.acceptedAt) : null
  const subtitleText =
    isDraftAccepted && acceptedAtDate
      ? t('modals:policies.acceptedAt', { date: acceptedAtDate })
      : t(`deals-modals:policies.${policy.key}.desc`)

  return (
    <ModalBottom
      open={open}
      close={close}
      center
      body={
        <View className="mx-8 mt-8 overflow-hidden rounded-[32px] border border-black/10">
          <View className="relative items-center gap-4 px-16 py-8">
            <Gradient
              colors={['#f3f4f7', '#ffffff', '#ffffff']}
              locations={[0, 0.5, 1]}
              start={{ x: 0, y: 0.5 }}
              end={{ x: 1, y: 0.5 }}
            />
            <View className="flex-row items-center gap-2">
              <View
                className="items-center justify-center rounded-2xl px-3 py-1.5"
                style={{
                  backgroundColor: isDraftAccepted ? colors.green[50] : '#e6e9ef',
                }}
              >
                <Typography
                  variant="XSB"
                  className="uppercase"
                  style={{
                    letterSpacing: 0.6,
                    color: isDraftAccepted ? colors.green[600] : '#595f73',
                  }}
                >
                  {isDraftAccepted
                    ? t('modals:policies.accepted')
                    : t('modals:policies.notAccepted')}
                </Typography>
              </View>
              {policy.required && (
                <View
                  className="items-center justify-center rounded-2xl px-3 py-1.5"
                  style={{ backgroundColor: '#ffedd5' }}
                >
                  <Typography
                    variant="XSB"
                    className="uppercase"
                    style={{ letterSpacing: 0.6, color: colors.orange[500] }}
                  >
                    {t('ui:required')}
                  </Typography>
                </View>
              )}
            </View>

            <Typography variant="LGB" color={colors.text} className="text-center">
              {t(`deals-modals:policies.${policy.key}.title`)}
            </Typography>

            <Typography variant="MD" color={colors.text50} className="text-center">
              {subtitleText}
            </Typography>
          </View>

          <View className="h-px w-full bg-black/10" />

          <ScrollView className="max-h-64 p-8">
            <FormattedText
              variant="XS"
              i18nKey={`create.${policy.key}.text`}
              ns="auth"
            />
          </ScrollView>
        </View>
      }
      buttons={
        <>
          <Button
            variant="outlined"
            label={t('ui:close')}
            iconEnd="x"
            onPress={close}
          />
          {!isDraftAccepted && (
            <Button
              variant="filled"
              label={t('ui:accept')}
              iconEnd="check"
              onPress={onAccept}
            />
          )}
        </>
      }
    />
  )
}
